import React, { useCallback, useMemo } from 'react';

import { useGlobalSearchParams, useRouter } from 'expo-router';
import { ICartEntry } from 'features/cart/types';
import { isRewardCartEntry, mapCartEntriesToLoyaltyCartEntries } from 'features/cart/utils';
import { LayoutChangeEvent, StyleSheet, Text, View } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { LoyaltyPromotionType, useLoyaltyUserBestPromotionQuery } from 'generated/graphql-gateway';
import {
  ISystemwidePromotionMessageQuery,
  useSystemwidePromotionMessageQuery,
} from 'generated/sanity-graphql';
import { useAuthRequiredInModal } from 'pages/authentication/components/auth-required';
import { ActivatablePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/activatable-promo-ribbon';
import { SimplePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/simple-promo-ribbon';
import { ModalAuthTrigger } from 'state/auth/types';
import { selectors, useAppSelector } from 'state/global-state';
import { IAppliedRewards, ICartEntryAdapter } from 'state/loyalty/hooks/types';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { parseEntry } from 'state/loyalty/hooks/utils/incentive-evaluation';
import { useUIContext } from 'state/ui';
import { ZIndex, primitive } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';

type SanityPromotion = ISystemwidePromotionMessageQuery['SystemwidePromotion'];

function isLegacyCartEntry(entry: unknown): entry is ILegacyCartEntry {
  // Checking for _id to differentiate between LegacyCartEntry and CartEntry
  return entry !== null && typeof entry === 'object' && '_id' in entry;
}

function isLegacyCartEntries(entries: unknown[]): entries is ILegacyCartEntry[] {
  return entries.length === 0 || isLegacyCartEntry(entries[0]);
}

function parseCartEntries(
  cartEntries: ILegacyCartEntry[] | ICartEntry[] | undefined,
  appliedRewards: IAppliedRewards
): ICartEntryAdapter[] | undefined {
  // Intentionally passing undefined cartEntries when cartEntries is undefined or empty
  // to skip cart requirements check in BE
  if (cartEntries === undefined || cartEntries.length === 0) {
    return undefined;
  }

  if (isLegacyCartEntries(cartEntries)) {
    return cartEntries
      .filter(entry => !appliedRewards[entry.lineId])
      .reduce(parseEntry({ type: LoyaltyPromotionType.SYSTEMWIDE_PROMOTION }), []);
  }

  return mapCartEntriesToLoyaltyCartEntries(
    cartEntries.filter(entry => !isRewardCartEntry(entry)),
    LoyaltyPromotionType.SYSTEMWIDE_PROMOTION
  );
}

let alreadyLoadedOnce = false;

export const PromoMessageRibbon = ({
  cartEntries,
  rounded = false,
  skipStoreDayPartHours = false,
}: {
  cartEntries?: ILegacyCartEntry[] | ICartEntry[];
  rounded?: boolean;
  skipStoreDayPartHours?: boolean;
}) => {
  const { activatePromo } = useGlobalSearchParams<{ activatePromo?: string }>();
  const router = useRouter();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser, loading: loyaltyLoading } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;
  const autoActivate = activatePromo === 'true';

  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);

  useAuthRequiredInModal({
    skip: !loyaltyEnabled || !autoActivate,
    trigger: ModalAuthTrigger.Global,
    // Prevent the modal from being displayed again on page refresh after user closes it
    onModalClose: () => router.setParams({ activatePromo: '' }),
  });

  const { promoMessageHeightRef } = useUIContext();

  const parsedCartEntries = useMemo(() => parseCartEntries(cartEntries, appliedRewards), [
    cartEntries,
    appliedRewards,
  ]);

  const {
    data: bestPromotionData,
    refetch: refetchBestPromotion,
  } = useLoyaltyUserBestPromotionQuery({
    variables: {
      loyaltyId,
      where: {
        includePersonalized: true,
        skipStoreDayPartHours,
        cartEntries: parsedCartEntries,
      },
    },
    skip: !loyaltyEnabled || !loyaltyId,
    fetchPolicy: 'cache-and-network',
    onCompleted: data => {
      const promotion = data?.loyaltyUserV2?.bestPromotion;

      // Clear the activatePromo param if the promotion is already activated or
      // doesn't require activation
      if (activatePromo && promotion && (!promotion?.mustActivate || promotion?.userActivatedAt)) {
        router.setParams({ activatePromo: '' });
      }
    },
  });

  const bestPromotion = bestPromotionData?.loyaltyUserV2?.bestPromotion;
  const { id: promotionId, sanityId, mustActivate, userActivatedAt } = bestPromotion ?? {};

  // alreadyLoadedOnce is a hack to prevent skipping the query which makes it null and flashing occurs
  const skip = !sanityId || (!alreadyLoadedOnce && (!loyaltyEnabled || loyaltyLoading));

  const { data } = useSystemwidePromotionMessageQuery({
    variables: { id: sanityId },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const sanityPromotion = data?.SystemwidePromotion;

  const handleActivated = useCallback(async () => {
    await refetchBestPromotion();
    if (autoActivate) {
      router.setParams({ activatePromo: '' });
    }
  }, [refetchBestPromotion, autoActivate, router]);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      promoMessageHeightRef.current = event.nativeEvent.layout.height;
    },
    [promoMessageHeightRef]
  );

  if (data) {
    alreadyLoadedOnce = true;
  }

  const message = mustActivate
    ? renderRibbonMessage({ sanityPromotion, userActivatedAt, mustActivate })
    : renderRibbonMessage({ sanityPromotion });

  if (!message) {
    promoMessageHeightRef.current = 0;
    return null;
  }

  return (
    <View onLayout={handleLayout}>
      {mustActivate ? (
        <ActivatablePromoRibbon
          sanityPromotion={sanityPromotion}
          message={message}
          loyaltyId={loyaltyId!}
          promotionId={promotionId!}
          userActivatedAt={userActivatedAt}
          onActivated={handleActivated}
          autoActivate={autoActivate}
          rounded={rounded}
        />
      ) : (
        <SimplePromoRibbon message={message} rounded={rounded} />
      )}
    </View>
  );
};

function renderRibbonMessage({
  userActivatedAt,
  sanityPromotion,
  mustActivate,
}: {
  userActivatedAt?: string | null;
  sanityPromotion?: SanityPromotion;
  mustActivate?: boolean | null;
}): React.ReactElement | null {
  const promoMessage = sanityPromotion?.promoMessage?.locale;
  const blockContent = userActivatedAt
    ? sanityPromotion?.activatedPromoMessageBlock?.locale
    : sanityPromotion?.promoMessageBlock?.locale;

  if (blockContent) {
    return (
      <SanityBlockRenderer
        content={blockContent}
        style={styles.blockRendererWrapper}
        textProps={{
          color: primitive.$bbqBrown,
          textAlign: mustActivate ? 'left' : 'center',
          fontSize: fontSizes.xs,
        }}
      />
    );
  }

  if (promoMessage) {
    return (
      <Text style={[styles.ribbonText, mustActivate && styles.activablePromotionText]}>
        {promoMessage}
      </Text>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  blockRendererWrapper: {
    zIndex: ZIndex.MAX,
  },
  ribbonText: {
    fontFamily: 'Flame-Regular',
    fontSize: fontSizes.xs,
    color: primitive.$bbqBrown,
    textAlign: 'center',
    zIndex: ZIndex.MAX,
  },
  activablePromotionText: {
    textAlign: 'left',
  },
});
